import { inject, Injectable, isDevMode } from '@angular/core';
import {
  addRxPlugin,
  createRxDatabase,
  DeepReadonlyObject,
  RxDocument,
  HashFunction,
} from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { RxDBAttachmentsPlugin } from 'rxdb/plugins/attachments';
import { SURVEY_VERSION_SCHEMA } from './schemas/survey-version.schema';
import { SURVEY_SESSION_SCHEMA } from './schemas/survey-session.schema';
import {
  SurveyCollections,
  SurveyDB,
  SurveySessionDoc,
} from './interfaces/embed-db.interface';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { SyncStatus } from '@hyper-inferno/survey-bot-core';
import { debounceTime, firstValueFrom } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@hyper-inferno/hyper-base-ng';
import { DeviceService } from '../device.service';

import { sha256 } from 'js-sha256';

// This is the "Polyfill" logic
export const myHashFunction: HashFunction = async (
  input: string | Blob | ArrayBuffer,
) => {
  if (typeof input === 'string') {
    return sha256(input);
  }

  if (input instanceof Blob) {
    // Convert Blob to ArrayBuffer
    const buffer = await input.arrayBuffer();
    return sha256(buffer);
  }

  // If it's an ArrayBuffer
  return sha256(input);
};

@Injectable({ providedIn: 'root' })
export class RxDBProviderService {
  public db!: SurveyDB;

  device = inject(DeviceService);

  private readonly API_URL = environment.interviewer_api;

  constructor(private http: HttpClient) {} // Inject here

  async init(): Promise<SurveyDB> {
    if (this.db) return this.db;

    // if (isDevMode()) {
    const { RxDBDevModePlugin } = await import('rxdb/plugins/dev-mode');
    addRxPlugin(RxDBDevModePlugin);
    // }
    addRxPlugin(RxDBLeaderElectionPlugin);
    addRxPlugin(RxDBAttachmentsPlugin);

    // 2. Pass the collection map type as a generic here

    this.db = await createRxDatabase<SurveyCollections>({
      name: 'infer_embed_db',
      storage: wrappedValidateAjvStorage({
        storage: getRxStorageDexie(),
      }),
      ignoreDuplicate: true,
      hashFunction: myHashFunction, // <--- THIS FIXES THE UT8 ERROR
    });

    await this.db.addCollections({
      // Make sure the keys match your SurveyCollections type exactly
      survey_versions: { schema: SURVEY_VERSION_SCHEMA },
      survey_sessions: { schema: SURVEY_SESSION_SCHEMA },
    });

    this.setupReplication();

    return this.db;
  }

  private async setupReplication() {
    // 1. Wait for this tab to be the "Leader"
    // This prevents 3 tabs from syncing at once and creating 3 log entries!
    await this.db.waitForLeadership();

    // 2. Start the watcher for survey_sessions
    this.db.survey_sessions
      .find({
        selector: {
          sync_status: { $in: ['dirty', 'error'] },
        },
      })
      .$.pipe(debounceTime(2000))
      .subscribe(async (unsyncedDocs) => {
        for (const doc of unsyncedDocs) {
          await this.syncSessionToGateway(doc);
        }
      });
  }

  private async syncSessionToGateway(doc: RxDocument<SurveySessionDoc, {}>) {
    if (
      doc.sync_status === SyncStatus.SYNCING ||
      doc.sync_status === SyncStatus.SYNCED
    ) {
      return;
    }

    try {
      // 1. Move to SYNCING state to lock the document locally
      await doc.incrementalPatch({ sync_status: SyncStatus.SYNCING });

      const payload = {
        sessionId: doc.id,
        surveyId: doc.survey_id,
        fullData: doc.toJSON(),
        updatedAt: doc.updated_at,
        device_info: await this.device.getDeviceInfo(),
      };

      // 3. Perform the POST via HttpClient
      // We convert the Observable to a Promise using firstValueFrom

      await this.attemptLiveSync(payload);

      // 4. SUCCESS: Update status
      await doc.incrementalPatch({
        sync_status: SyncStatus.SYNCED,
        last_sync_time: new Date().toISOString(),
      });

      console.log(`✅ Session ${doc.id} synced.`);
    } catch (err) {
      console.error('Sync attempt failed', err);

      // 4. FAILURE: Update status to ERROR
      // The subscription watcher will pick this up again later for retry
      await doc.incrementalPatch({ sync_status: SyncStatus.ERROR });
    }
  }

  async attemptLiveSync(payload: {
    sessionId: string;
    surveyId: number;
    fullData: DeepReadonlyObject<SurveySessionDoc>;
    updatedAt: string;
    device_info: any;
  }) {
    await firstValueFrom(
      this.http.post(`${this.API_URL}/sync-session`, payload),
    );
  }
}
